import React, { useEffect, useMemo } from "react";
import { ItemGroupSummary, PurchaseHistory, PickUpPage, OrderLifecycle } from "../component.d";
import GenerateOrderSummaryItems from "../common/GenerateOrderSummaryItems";
import { AboveTheFoldMarker, CriticalFeatureMarker, FunctionalMarker } from "../csa/markers";
import { QRCodeCanvas } from "qrcode.react";
import ContactCustomerCare from "../common/ContactCustomerCare";
import { parseBoldMarkers } from "./LabelUtils";
import { PickupInstructionsBreadCrumb } from "../common/BreadCrumb";
import appMetrics from "../../common/MetricsService";
import MetricsConstants from "../../common/MetricsConstants";
import "./PickupInstructionsPage.scss";
import * as KatalMetrics from "@amzn/katal-metrics";

interface PickupInstructionsPageProps {
    itemGroupSummary: ItemGroupSummary;
    purchaseHistory: PurchaseHistory;
    pickupPage: PickUpPage;
}

interface BackToOneMedicalButtonProps {
    label: string;
    url: string;
    id: string;
    onClick: () => void;
}

function BackToOneMedicalButton({ label, url, id, onClick }: BackToOneMedicalButtonProps) {
    return (
        <pui-button
            label={label}
            theme="secondary"
            style={{ textAlign: 'center' }}
            url={url}
            id={id}
            data-csa-c-type="button"
            data-csa-c-action="navigate"
            data-csa-c-content-id={id}
            data-csa-c-slot-id="main"
            onClick={onClick}
        />
    );
}

function PickupInstructionsPage(props: PickupInstructionsPageProps) {
    const backToOneMedicalUrl = useMemo(
        () => document.getElementById('root')?.dataset.bwapOriginUrl,
        []
    );

    const handleBackToOneMedicalClick = () => {
        const metric = new KatalMetrics.Metric.Counter(MetricsConstants.METRIC_BACK_TO_ONE_MEDICAL_CLICK).withMonitor();
        metric.add(1);
        const publisher = appMetrics.getMetricsPublisherForMethod("PickupInstructions");
        if (publisher && publisher.publish) {
            publisher.publish(metric);
        }
    };

    // Utility function to determine QR status based on orderStatus and qrString availability
    const getQRStatus = () => {
        const { orderStatus } = props.itemGroupSummary;
        const { qrString } = props.pickupPage.qrCode;
        const { labels } = props.pickupPage;

        // Check if QR string is null or undefined
        if (!qrString || qrString.trim() === '') {
            return {
                showQR: false,
                primaryMessage: labels.kioskQrCodeUnavailable,
                secondaryMessage: labels.kioskQrTryAgain
            };
        }

        // Check orderStatus conditions
        switch (orderStatus) {
            case OrderLifecycle.OrderedArrivingSameDay:
                return {
                    showQR: false,
                    primaryMessage: labels.kioskQrOrderStillProcessing,
                    secondaryMessage: labels.kioskQrWaiting
                };
            case OrderLifecycle.DelayedMiss1:
                return {
                    showQR: false,
                    primaryMessage: labels.kioskQrOrderTakingLonger,
                    secondaryMessage: labels.kioskQrCustomerCare
                };
            default:
                // For all other orderStatus values, show the QR code
                return {
                    showQR: true,
                    primaryMessage: labels.kioskScanQRMsg,
                    secondaryMessage: null
                };
        }
    };

    // Publish QR code visibility metric on component mount. This metric is emitted only on first page load,
    // not on QR code refresh.
    // See business metric requirements here: https://quip-amazon.com/8sd8A0PykTug/Kiosks-Launch-Biz-Metrics
    useEffect(() => {
        const qrStatus = getQRStatus();
        // Publish QR visibility metric
        const metricName = qrStatus.showQR
            ? MetricsConstants.METRIC_QR_CODE_VISIBLE
            : MetricsConstants.METRIC_QR_CODE_HIDDEN;

        // Include withMonitor to enable rendering metric on Monitor Portal
        const attemptMetric =
            new KatalMetrics.Metric.Counter(metricName).withMonitor();
        attemptMetric.add(1);

        const publisher =
            appMetrics.getMetricsPublisherForMethod("PickupInstructions");
        
        if (publisher && publisher.publish) {
            publisher.publish(attemptMetric);
        }
    }, []);

    // Render QR code or status message based on conditions
    const renderQRCodeOrStatus = () => {
        const qrStatus = getQRStatus();

        if (qrStatus.showQR) {
            // Show actual QR code
            return (
                <div className="qr-code-container">
                    <QRCodeCanvas
                        value={props.pickupPage.qrCode.qrString}
                        size={140}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"M"}
                        includeMargin={false}
                    />
                </div>
            );
        } else {
            // Show status message instead of QR code
            return (
                <div className="qr-status-container">
                    <pui-stack direction="vertical" spacing="small" secondaryAxisArrangement="center">
                        <pui-heading input={qrStatus.primaryMessage} spacingBottom="small" textSize="small" textalign="center" />
                        <pui-text input={qrStatus.secondaryMessage} textSize="medium" textalign="center" />
                    </pui-stack>
                </div>
            );
        }
    };

    return (
        <div>
            {/* QR Code Section - Left aligned */}
            <pui-stack direction="vertical" spacingBottom="medium">
                <PickupInstructionsBreadCrumb
                    yourOrders={props.purchaseHistory.labels.yourOrders}
                    pickUpInstructionsLabel={props.pickupPage.labels.kioskPickUpInstructionsLabel}
                />
                {renderQRCodeOrStatus()}
                <pui-heading input={props.pickupPage.labels.kioskScanQRMsg} headingLevel="1" textSize="large"
                             spacingBottom="mini" spacingTop="medium"/>
            </pui-stack>

            {/* Pickup Location Section */}
            <pui-stack direction="vertical" spacingBottom="large">
                <pui-heading input={props.pickupPage.labels.kioskPickUpMsg} textSize="small" spacingBottom="mini"
                             spacingTop="mini"/>
                <pui-text input={props.pickupPage.instructions.kioskPickUpAddress}/>
                <pui-text encode input={props.pickupPage.instructions.kioskPickUpHours}
                          style={{whiteSpace: "pre-line"}}/>
            </pui-stack>

            {/* Reservation Information - Highlighted box */}
            <pui-banner status="warning-solid" class="pui-element" spacingTop="medium" spacingBottom="medium">
                <pui-stack>
                    <pui-text input={parseBoldMarkers(props.pickupPage.instructions.kioskCancelRefundMsg)} />
                </pui-stack>
            </pui-banner>

            <AboveTheFoldMarker />
            <CriticalFeatureMarker />

            {/* Help Section */}
            <pui-stack spacingTop="medium" spacingBottom="medium">
                <pui-heading input={props.pickupPage.labels.kioskOrderHelpLabel} headingLevel="2" textSize="medium" spacingBottom="small" />
                <pui-text input={props.pickupPage.labels.kioskOrderHelpMsg} spacingBottom="small" />
            </pui-stack>

            {/* Back to One Medical Button */}
            {backToOneMedicalUrl && (
                <>
                    <pui-section class="mobile-only" style={{ padding: '0' }}>
                        <pui-section padding="medium">
                            <BackToOneMedicalButton
                                label={props.pickupPage.labels.backToOneMedicalLabel}
                                url={backToOneMedicalUrl}
                                id="button-to-one-medical-mobile"
                                onClick={handleBackToOneMedicalClick}
                            />
                        </pui-section>
                    </pui-section>
                    <pui-section padding="medium" class="tablet-and-desktop-only">
                        <BackToOneMedicalButton
                            label={props.pickupPage.labels.backToOneMedicalLabel}
                            url={backToOneMedicalUrl}
                            id="button-to-one-medical-desktop"
                            onClick={handleBackToOneMedicalClick}
                        />
                    </pui-section>
                </>
            )}

            <ContactCustomerCare contactCustomerCare={props.pickupPage.labels.kioskCustomerCareLabel} />

            <pui-divider spacingBottom="medium" />

            {/* Order Summary Section */}
            <pui-heading input={props.pickupPage.labels.orderSummary} headingLevel="2" textSize="medium"
                         spacingBottom="mini" spacingTop="medium"/>
            <GenerateOrderSummaryItems
                itemGroupSummary={props.itemGroupSummary}
                isPillPackComDelivery={false}
            />
            <pui-divider spacingTop="small"></pui-divider>

            <FunctionalMarker />
        </div>
    );
}

export default PickupInstructionsPage;
